import { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  Plus,
  Send,
  Search,
  Archive,
  Bot,
  User,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useMessageThreads,
  useMessageThread,
  useCreateThread,
  useSendMessage,
  useMarkThreadRead,
  useMessageUnreadCount,
  useClients,
  useArchiveThread,
} from '@/hooks/queries';

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Τώρα';
  if (minutes < 60) return `${minutes}λ πριν`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}ω πριν`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}η πριν`;
  return new Date(dateStr).toLocaleDateString('el-GR');
}

export function MessagesPage() {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [newThreadDialogOpen, setNewThreadDialogOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState('');
  const [mobileView, setMobileView] = useState<'list' | 'thread'>('list');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // New thread form state
  const [newThreadClientId, setNewThreadClientId] = useState('');
  const [newThreadSubject, setNewThreadSubject] = useState('');
  const [newThreadBody, setNewThreadBody] = useState('');

  // Queries
  const { data: unreadData } = useMessageUnreadCount();
  const { data: threadsData, isLoading: threadsLoading } = useMessageThreads({
    search: search || undefined,
    isArchived: showArchived ? 'true' : undefined,
  });
  const { data: threadData, isLoading: threadLoading } = useMessageThread(selectedThreadId);
  const { data: clientsData } = useClients({ limit: 200 });

  // Mutations
  const createThread = useCreateThread();
  const sendMessage = useSendMessage();
  const markThreadRead = useMarkThreadRead();
  const archiveThread = useArchiveThread();

  // Auto-mark thread as read when selected
  useEffect(() => {
    if (selectedThreadId && threadData) {
      const isUnread =
        threadData.lastMessageAt &&
        (!threadData.lastOfficeReadAt || threadData.lastMessageAt > threadData.lastOfficeReadAt);
      if (isUnread) {
        markThreadRead.mutate(selectedThreadId);
      }
    }
  }, [selectedThreadId, threadData?.lastMessageAt]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadData?.messages?.data?.length]);

  const handleCreateThread = () => {
    if (!newThreadClientId || !newThreadBody.trim()) return;
    createThread.mutate(
      {
        clientId: newThreadClientId,
        subject: newThreadSubject.trim() || undefined,
        body: newThreadBody.trim(),
      },
      {
        onSuccess: (data: any) => {
          setNewThreadDialogOpen(false);
          setNewThreadClientId('');
          setNewThreadSubject('');
          setNewThreadBody('');
          setSelectedThreadId(data.id);
          setMobileView('thread');
        },
      },
    );
  };

  const handleSendMessage = () => {
    if (!selectedThreadId || !composeDraft.trim()) return;
    sendMessage.mutate(
      { threadId: selectedThreadId, body: composeDraft.trim() },
      { onSuccess: () => setComposeDraft('') },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const threads = threadsData?.data ?? [];
  const threadMessages = threadData?.messages?.data ?? [];

  return (
    <div className="flex h-[calc(100vh)] overflow-hidden">
      {/* Left Panel - Thread List */}
      <div
        className={`w-full md:w-80 border-r bg-surface-2 rounded-[12px] border border-border flex flex-col flex-shrink-0 ${
          mobileView === 'thread' ? 'hidden md:flex' : 'flex'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              <h1 className="text-lg font-semibold tracking-tight font-display">Μηνύματα</h1>
              {unreadData && unreadData.unreadCount > 0 ? (
                <Badge variant="default">{unreadData.unreadCount}</Badge>
              ) : null}
            </div>
            <Dialog open={newThreadDialogOpen} onOpenChange={setNewThreadDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Νέο Νήμα
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display">Νέο Νήμα</DialogTitle>
                  <DialogDescription>Ξεκινήστε μια νέα συνομιλία με πελάτη</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Πελάτης *</label>
                    <Select value={newThreadClientId} onValueChange={setNewThreadClientId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Επιλέξτε πελάτη" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientsData?.data.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.businessName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Θέμα</label>
                    <Input
                      placeholder="π.χ. Έγγραφα Φεβρουαρίου"
                      value={newThreadSubject}
                      onChange={(e) => setNewThreadSubject(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Μήνυμα *</label>
                    <Textarea
                      placeholder="Γράψτε μήνυμα..."
                      value={newThreadBody}
                      onChange={(e) => setNewThreadBody(e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewThreadDialogOpen(false)}>Ακύρωση</Button>
                  <Button onClick={handleCreateThread} disabled={!newThreadClientId || !newThreadBody.trim() || createThread.isPending}>
                    <Send className="w-4 h-4 mr-1" />
                    Αποστολή
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Αναζήτηση..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-full border-border bg-surface-2"
            />
          </div>

          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
              showArchived ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Archive className="w-3.5 h-3.5" />
            {showArchived ? 'Αρχειοθετημένα' : 'Εμφάνιση αρχειοθετημένων'}
          </button>
        </div>

        {/* Thread List */}
        <div className="flex-1 overflow-y-auto">
          {threadsLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Καμία συνομιλία</p>
            </div>
          ) : (
            <div className="divide-y">
              {threads.map((thread) => {
                const isUnread =
                  thread.lastMessageAt &&
                  (!thread.lastOfficeReadAt || thread.lastMessageAt > thread.lastOfficeReadAt);
                const isSelected = thread.id === selectedThreadId;

                return (
                  <button
                    key={thread.id}
                    onClick={() => {
                      setSelectedThreadId(thread.id);
                      setMobileView('thread');
                    }}
                    className={`w-full text-left p-3 hover:bg-muted/50 transition-colors ${
                      isSelected ? 'bg-brand-soft border-l-2 border-l-primary' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {isUnread ? (
                          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                        ) : null}
                        <span className={`text-sm truncate ${isUnread ? 'font-semibold' : 'font-medium'}`}>
                          {thread.clientBusinessName}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground font-data flex-shrink-0">
                        {thread.lastMessageAt ? timeAgo(thread.lastMessageAt) : ''}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate pl-4">
                      {thread.subject || thread.lastMessagePreview || '—'}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Thread View */}
      <div
        className={`flex-1 flex flex-col ${
          mobileView === 'list' ? 'hidden md:flex' : 'flex'
        }`}
      >
        {selectedThreadId && threadData ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileView('list')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold truncate font-display">{threadData.clientBusinessName}</h2>
                {threadData.subject ? (
                  <p className="text-sm text-muted-foreground truncate">{threadData.subject}</p>
                ) : null}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  archiveThread.mutate({
                    threadId: selectedThreadId,
                    isArchived: !threadData.isArchived,
                  });
                }}
              >
                <Archive className="w-4 h-4 mr-1" />
                {threadData.isArchived ? 'Επαναφορά' : 'Αρχειοθέτηση'}
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {threadLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-3/4" />
                  ))}
                </div>
              ) : threadMessages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <p className="text-sm">Δεν υπάρχουν μηνύματα</p>
                </div>
              ) : (
                threadMessages.map((msg) => {
                  const isOffice = msg.direction === 'OFFICE_TO_CLIENT';
                  const senderName = msg.senderUserName || msg.senderContactName || 'Σύστημα';

                  if (msg.isAutomated) {
                    return (
                      <div key={msg.id} className="flex justify-center">
                        <div className="bg-muted/50 rounded-lg px-4 py-2 flex items-center gap-2 max-w-md">
                          <Bot className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div>
                            <p className="text-sm text-muted-foreground">{msg.body}</p>
                            <p className="text-xs text-muted-foreground/60 mt-1">
                              {new Date(msg.createdAt).toLocaleString('el-GR', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className={`flex gap-2 ${isOffice ? 'justify-end' : 'justify-start'}`}>
                      {!isOffice ? (
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-3.5 h-3.5" />
                        </div>
                      ) : null}
                      <div className={`max-w-md rounded-[12px] px-3 py-2 ${
                        isOffice ? 'bg-primary text-primary-foreground' : 'bg-card border border-border'
                      }`}>
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="text-xs font-medium">{senderName}</span>
                          <span className={`text-xs text-muted-foreground ${isOffice ? 'opacity-70' : ''}`}>
                            {new Date(msg.createdAt).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                      </div>
                      {isOffice ? (
                        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                          <User className="w-3.5 h-3.5 text-primary-foreground" />
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Compose */}
            <div className="p-4 border-t border-border/40 bg-card/50">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Γράψτε μήνυμα... (Ctrl+Enter για αποστολή)"
                  value={composeDraft}
                  onChange={(e) => setComposeDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  className="flex-1 resize-none rounded-full border-border bg-surface-2 px-4"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!composeDraft.trim() || sendMessage.isPending}
                  size="icon"
                  className="self-end rounded-full gradient-primary text-white"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium font-display">Επιλέξτε μια συνομιλία</p>
              <p className="text-sm mt-1">ή δημιουργήστε νέο νήμα</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
